import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  CreditCard,
  Edit,
  Check,
  Loader2
} from 'lucide-react';

const API = RUNTIME_BACKEND_URL;

export default function AdminPlansPage() {
  const { token } = useAuthStore();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price_monthly: '',
    price_yearly: '',
    features: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const res = await fetch(`${API}/api/admin/subscription-plans`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPlans(data);
      }
    } catch (error) {
      console.error('Paketler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      price_monthly: plan.price_monthly?.toString() || '',
      price_yearly: plan.price_yearly?.toString() || '',
      features: plan.features?.join('\n') || ''
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingPlan) return;

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API}/api/admin/subscription-plans/${editingPlan.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          price_monthly: parseFloat(formData.price_monthly) || 0,
          price_yearly: parseFloat(formData.price_yearly) || 0,
          features: formData.features.split('\n').filter(f => f.trim())
        })
      });
      
      if (!res.ok) throw new Error('Kaydedilemedi');
      
      toast.success('Paket güncellendi');
      setIsDialogOpen(false);
      loadPlans();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-plans-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Abonelik Paketleri</h1>
        <p className="text-muted-foreground mt-1">Kullanıcılara sunulan paketleri düzenleyin</p>
      </div>

      {/* Plans Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {plans.map((plan) => (
          <Card key={plan.id} className="relative">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  {plan.name}
                </div>
                <Button variant="ghost" size="icon" onClick={() => handleEdit(plan)}>
                  <Edit className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Prices */}
              <div className="space-y-4">
                {plan.price_monthly > 0 && (
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Aylık</p>
                    <p className="text-3xl font-bold text-primary">{plan.price_monthly.toLocaleString('tr-TR')} TL</p>
                  </div>
                )}
                {plan.price_yearly > 0 && (
                  <div className="text-center p-4 bg-primary/10 rounded-lg">
                    <p className="text-sm text-muted-foreground">Yıllık</p>
                    <p className="text-3xl font-bold text-primary">{plan.price_yearly.toLocaleString('tr-TR')} TL</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      (Aylık {Math.round(plan.price_yearly / 12).toLocaleString('tr-TR')} TL)
                    </p>
                  </div>
                )}
              </div>

              {/* Features */}
              <div className="space-y-2">
                <p className="font-medium text-sm">Özellikler:</p>
                <ul className="space-y-2">
                  {plan.features?.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Paket Düzenle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Paket Adı</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Aylık Fiyat (TL)</Label>
                <Input
                  type="number"
                  value={formData.price_monthly}
                  onChange={(e) => setFormData({ ...formData, price_monthly: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Yıllık Fiyat (TL)</Label>
                <Input
                  type="number"
                  value={formData.price_yearly}
                  onChange={(e) => setFormData({ ...formData, price_yearly: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Özellikler (her satıra bir özellik)</Label>
              <textarea
                className="w-full min-h-[150px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={formData.features}
                onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                placeholder="Sınırsız müşteri&#10;Sınırsız albüm&#10;..."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1">
                İptal
              </Button>
              <Button onClick={handleSave} disabled={isSubmitting} className="flex-1">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
